import {
  deferOnboardingSystemNotice,
  getDeferredSystemNotice,
  removeDeferredSystemNotice,
  systemNoticePresented,
  systemNoticeRemoved,
  installOnboardingSystemNoticeGate,
} from "@/lib/onboarding-system-notices";
import type { SystemToast, SystemToastInput } from "./notification-types";
import type { NotificationState } from "./notification-store";
import {
  forwardSystemToastAddition,
  forwardSystemToastDismissal,
} from "@/lib/system-toast-dismissal";
import { subscribeLanguage } from "@/i18n";
import {
  withMutationEmitEnabled,
  withMutationEmitSuppressed,
} from "@/lib/mirrored-store";
type Store = {
  getState: () => NotificationState;
  setState: (
    patch:
      | Partial<NotificationState>
      | ((state: NotificationState) => Partial<NotificationState>),
  ) => void;
};
const MAX_EPHEMERAL_TOASTS = 3;

type TextBuilder = { revision: string; build: () => SystemToast };
/** Builders of the toasts this window raised, by id; local like the actions. */
const textBuilders = new Map<string, TextBuilder>();

/** The toast `input` shows now. A queued notice released after onboarding
 *  re-reads its builder, so it appears in the language of that moment. */
export function resolveSystemToast(input: SystemToastInput): {
  toast: SystemToast;
  build?: () => SystemToast;
} {
  if (typeof input === "function") return { toast: input(), build: input };
  const kept = textBuilders.get(input.id);
  if (!kept || !input.revision || kept.revision !== input.revision)
    return { toast: input };
  return {
    toast: { ...kept.build(), revision: input.revision },
    build: kept.build,
  };
}

/** Keep the builder of the occurrence just raised; a plain toast drops it. */
export function rememberSystemToastText(
  toast: SystemToast,
  build?: () => SystemToast,
): void {
  if (build && toast.revision)
    textBuilders.set(toast.id, { revision: toast.revision, build });
  else textBuilders.delete(toast.id);
}

/** On a language change, rewrite this window's live toasts in place: same
 *  occurrence (revision, actions, position), new text. */
export function installSystemToastTextRefresh(store: Store): void {
  subscribeLanguage(() => {
    const state = store.getState();
    for (const [id, { revision, build }] of textBuilders) {
      const current = state.systemToasts.find((toast) => toast.id === id);
      if (current?.revision !== revision) {
        const queued = state.deferredSystemToasts.some(
          (toast) => toast.id === id && toast.revision === revision,
        );
        if (!queued) textBuilders.delete(id);
        continue;
      }
      const { title, description, actionLabel, secondaryActionLabel } = build();
      const next = {
        ...current,
        title,
        description,
        actionLabel,
        secondaryActionLabel,
      };
      const replace = () =>
        store.setState((latest) => ({
          systemToasts: latest.systemToasts.map((toast) =>
            toast.id === id && toast.revision === revision ? next : toast,
          ),
        }));
      if (forwardSystemToastAddition(next, revision))
        withMutationEmitSuppressed(replace);
      else withMutationEmitEnabled(replace);
    }
  });
}
export function addToastToState(
  state: Pick<NotificationState, "systemToasts" | "systemToastActions">,
  toast: SystemToast,
  onAction?: () => void,
) {
  let ephemeral = 0;
  const systemToasts = [
    toast,
    ...state.systemToasts.filter((item) => item.id !== toast.id),
  ].filter((item) => item.persistent || ++ephemeral <= MAX_EPHEMERAL_TOASTS);
  const retainedIds = new Set(systemToasts.map((item) => item.id));
  const actions = new Map(
    [...state.systemToastActions].filter(([id]) => retainedIds.has(id)),
  );
  if (onAction) actions.set(toast.id, onAction);
  else actions.delete(toast.id);
  return { systemToasts, systemToastActions: actions };
}

/** The owner applies a toast a mirror raised, unless the owner already moved on. */
export function applyForwardedSystemToast(
  store: Store,
  toast: SystemToast,
  expectedRevision: string | null,
): void {
  const state = store.getState();
  const current = [...state.deferredSystemToasts, ...state.systemToasts].find(
    (item) => item.id === toast.id,
  );
  if (
    current?.revision === toast.revision &&
    expectedRevision === toast.revision
  ) {
    // The mirror that raised it rewrote its text in a new app language.
    store.setState({
      systemToasts: state.systemToasts.map((item) =>
        item === current ? toast : item,
      ),
    });
    return;
  }
  if (
    current?.revision === toast.revision ||
    (current?.revision ?? null) !== expectedRevision
  ) {
    // Reconcile the originating mirror too: its optimistic state may be stale.
    store.setState({ systemToasts: [...state.systemToasts] });
    return;
  }
  if (deferOnboardingSystemNotice(toast)) return;
  store.setState(addToastToState(state, toast));
  systemNoticePresented(toast);
}

export function installNotificationSystemNoticeGate(store: {
  getState: () => NotificationState;
  setState: (patch: Partial<NotificationState>) => void;
}) {
  installOnboardingSystemNoticeGate({
    readQueued: () => store.getState().deferredSystemToasts,
    writeQueued: (toasts) => store.setState({ deferredSystemToasts: toasts }),
    show: (toast, action) => store.getState().addSystemToast(toast, action),
    takeVisible: () => {
      const state = store.getState();
      const notices = state.systemToasts.map((toast) => ({
        toast,
        action: state.systemToastActions.get(toast.id),
      }));
      store.setState({ systemToasts: [], systemToastActions: new Map() });
      return notices;
    },
  });
}

/** Clear both queued and visible occurrences; a mirror asks the owner with the exact revision. */
export function removeSystemToastFromState(
  store: Store,
  id: string,
  expectedRevision?: string | null,
): boolean {
  const deferred =
    getDeferredSystemNotice(id) ??
    store.getState().deferredSystemToasts.find((toast) => toast.id === id);
  const deferredRemoved = removeDeferredSystemNotice(id, expectedRevision);
  const current = store
    .getState()
    .systemToasts.find((toast) => toast.id === id);
  if (
    !current ||
    (expectedRevision !== undefined &&
      (current.revision ?? null) !== expectedRevision)
  ) {
    if (
      deferred &&
      (expectedRevision === undefined ||
        (deferred.revision ?? null) === expectedRevision)
    )
      return (
        forwardSystemToastDismissal(id, deferred.revision ?? null) ||
        deferredRemoved
      );
    return deferredRemoved;
  }
  if (
    deferred &&
    expectedRevision === undefined &&
    deferred.revision !== current.revision
  )
    forwardSystemToastDismissal(id, deferred.revision ?? null);
  const remove = () =>
    store.setState((state) => {
      const actions = new Map(state.systemToastActions);
      actions.delete(id);
      return {
        systemToasts: state.systemToasts.filter((toast) => toast.id !== id),
        systemToastActions: actions,
      };
    });
  if (forwardSystemToastDismissal(id, current.revision ?? null))
    withMutationEmitSuppressed(remove);
  else withMutationEmitEnabled(remove);
  if (textBuilders.get(id)?.revision === current.revision)
    textBuilders.delete(id);
  systemNoticeRemoved(id);
  return true;
}
